import React, { useCallback } from 'react';
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Position,
  addEdge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

// ROLES constant needed for permission assignment
export const ROLES = ['Client', 'Développeur', 'Administrateur', 'Chef de projet', 'Testeur', 'Observateur'];

// Available node colors
// Couleurs DS « Cosmos ». Les `id` restent stables (les nœuds existants stockent
// l'id) ; seules les valeurs hex + libellés passent au DS.
const NODE_COLORS = [
  { id: 'gray', value: '#64748b', border: '#334155', label: 'Slate' },
  { id: 'blue', value: '#0ea5e9', border: '#0284c7', label: 'Ciel' },
  { id: 'green', value: '#10b981', border: '#047857', label: 'Émeraude' },
  { id: 'red', value: '#f43f5e', border: '#be123c', label: 'Rose' },
  { id: 'purple', value: '#8b5cf6', border: '#7c3aed', label: 'Violet' },
  { id: 'orange', value: '#f59e0b', border: '#b45309', label: 'Ambre' },
];

// Define initial nodes based on the diagram
const initialNodes = [
  {
    id: 'backlog',
    position: { x: 50, y: 50 },
    data: { label: 'BACKLOG' },
    style: { backgroundColor: '#a0aec0', color: 'white', border: '1px solid #718096', width: 150 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
  },
  {
    id: 'demand',
    position: { x: 250, y: 150 },
    data: { label: 'DEMAND' },
    style: { backgroundColor: '#a0aec0', color: 'white', border: '1px solid #718096', width: 150 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
  },
  {
    id: 'readyToDev',
    position: { x: 450, y: 250 },
    data: { label: 'READY TO DEV' },
    style: { backgroundColor: '#63b3ed', color: 'white', border: '1px solid #3182ce', width: 150 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
  },
  {
    id: 'inProgress',
    position: { x: 650, y: 350 },
    data: { label: 'IN PROGRESS' },
    style: { backgroundColor: '#63b3ed', color: 'white', border: '1px solid #3182ce', width: 150 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
  },
  {
    id: 'doneProducer',
    position: { x: 850, y: 450 },
    data: { label: 'DONE PRODUCER' },
    style: { backgroundColor: '#63b3ed', color: 'white', border: '1px solid #3182ce', width: 150 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
  },
  {
    id: 'validateInternal',
    position: { x: 1050, y: 550 },
    data: { label: 'VALIDATE INTERNAL' },
    style: { backgroundColor: '#63b3ed', color: 'white', border: '1px solid #3182ce', width: 150 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
  },
  {
    id: 'validateCustomer',
    position: { x: 1250, y: 650 },
    data: { label: 'VALIDATE CUSTOMER / DONE' },
    style: { backgroundColor: '#68d391', color: 'white', border: '1px solid #38a169', width: 200 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
  },
  {
    id: 'blocked',
    position: { x: 250, y: 550 },
    data: { label: 'BLOCKED' },
    style: { backgroundColor: '#e53e3e', color: 'white', border: '1px solid #c53030', width: 150 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
  },
  {
    id: 'canceled',
    position: { x: 250, y: 650 },
    data: { label: 'CANCELED' },
    style: { backgroundColor: '#e53e3e', color: 'white', border: '1px solid #c53030', width: 150 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
  },
];

// Define initial edges based on the diagram
const initialEdges = [
  // Forward flow
  { id: 'e-backlog-demand', source: 'backlog', target: 'demand', markerEnd: { type: MarkerType.ArrowClosed }, data: { allowedRoles: ['Chef de projet', 'Administrateur'] }, animated: true, style: { stroke: '#5a4fe0', strokeWidth: 3 } },
  { id: 'e-demand-ready', source: 'demand', target: 'readyToDev', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'e-ready-progress', source: 'readyToDev', target: 'inProgress', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'e-progress-done', source: 'inProgress', target: 'doneProducer', markerEnd: { type: MarkerType.ArrowClosed }, data: { allowedRoles: ['Développeur'] }, animated: true, style: { stroke: '#5a4fe0', strokeWidth: 3 } },
  { id: 'e-done-validate', source: 'doneProducer', target: 'validateInternal', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'e-validate-customer', source: 'validateInternal', target: 'validateCustomer', markerEnd: { type: MarkerType.ArrowClosed } },

  // Backward flow / Loops
  { id: 'e-demand-backlog', source: 'demand', target: 'backlog', type: 'step', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'e-ready-demand', source: 'readyToDev', target: 'demand', type: 'step', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'e-progress-ready', source: 'inProgress', target: 'readyToDev', type: 'step', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'e-done-progress', source: 'doneProducer', target: 'inProgress', type: 'step', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'e-validate-done', source: 'validateInternal', target: 'doneProducer', type: 'step', markerEnd: { type: MarkerType.ArrowClosed } },
  
  // Special loops (Validate Internal -> In Progress) - needs custom path or type
  { id: 'e-validate-progress', source: 'validateInternal', target: 'inProgress', type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } },

  // Side branches - disconnected by default as requested
  // { id: 'e-progress-blocked', source: 'inProgress', target: 'blocked', type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } },
  // { id: 'e-demand-canceled', source: 'demand', target: 'canceled', type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } },
  // { id: 'e-blocked-progress', source: 'blocked', target: 'inProgress', type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } },

  // Note labels (simulated with edge labels for now or just omitted for simplicity as they are verbose)
];

const WorkflowModal = ({ isOpen, onClose, title, readOnly = false, onSave, initialNodes: propInitialNodes, initialEdges: propInitialEdges }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(propInitialNodes || initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(propInitialEdges || initialEdges);
  
  const [selectedElement, setSelectedElement] = React.useState(null);
  const [isEditing, setIsEditing] = React.useState(false);

  // Sync state when props change (e.g. reopening with different workflow)
  React.useEffect(() => {
      if (isOpen) {
        setNodes(propInitialNodes || initialNodes);
        setEdges(propInitialEdges || initialEdges);
        setSelectedElement(null);
        setIsEditing(false);
      }
  }, [isOpen, propInitialNodes, propInitialEdges, setNodes, setEdges]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges],
  );

  const onNodeClick = (event, node) => {
      setSelectedElement({ type: 'node', data: node });
      setIsEditing(true);
  };

  const onEdgeClick = (event, edge) => {
      setSelectedElement({ type: 'edge', data: edge });
      setIsEditing(true);
  };

  const onPaneClick = () => {
      setSelectedElement(null);
      setIsEditing(false);
  };

  const updateNodeLabel = (label) => {
      if (!selectedElement || selectedElement.type !== 'node') return;
      
      const nodeId = selectedElement.data.id;
      const nodeIndex = nodes.findIndex(n => n.id === nodeId);
      if (nodeIndex === -1) return;

      const node = nodes[nodeIndex];
      const updatedNode = {
          ...node,
          data: { ...node.data, label: label }
      };

      const newNodes = [...nodes];
      newNodes[nodeIndex] = updatedNode;

      setNodes(newNodes);
      // Update selectedElement correctly to point to the new node object so input value updates
      setSelectedElement({ type: 'node', data: updatedNode });
  };

  const updateEdgeRoles = (role) => {
      if (!selectedElement || selectedElement.type !== 'edge') return;
      
      const edgeIndex = edges.findIndex(e => e.id === selectedElement.data.id);
      if (edgeIndex === -1) return;

      const edge = edges[edgeIndex];
      const currentRoles = edge.data?.allowedRoles || [];
      const newRoles = currentRoles.includes(role)
          ? currentRoles.filter(r => r !== role)
          : [...currentRoles, role];
      
      const updatedEdge = {
          ...edge,
          data: { ...edge.data, allowedRoles: newRoles },
          // Visual feedback for restrictions
          style: { ...edge.style, stroke: newRoles.length > 0 ? '#5a4fe0' : '#b1b1b7', strokeWidth: newRoles.length > 0 ? 3 : 1 },
          animated: newRoles.length > 0
      };

      const newEdges = [...edges];
      newEdges[edgeIndex] = updatedEdge;

      setEdges(newEdges);
      setSelectedElement({ type: 'edge', data: updatedEdge });
  };

  const updateNodeColor = (colorId) => {
      if (!selectedElement || selectedElement.type !== 'node') return;
      
      const colorObj = NODE_COLORS.find(c => c.id === colorId);
      if (!colorObj) return;

      const nodeId = selectedElement.data.id;
      const nodeIndex = nodes.findIndex(n => n.id === nodeId);
      if (nodeIndex === -1) return;

      const node = nodes[nodeIndex];
      const updatedNode = {
          ...node,
          style: { 
              ...node.style, 
              backgroundColor: colorObj.value, 
              borderColor: colorObj.border 
          }
      };

      const newNodes = [...nodes];
      newNodes[nodeIndex] = updatedNode;

      setNodes(newNodes);
      setSelectedElement({ type: 'node', data: updatedNode });
  };

  const addNewNode = () => {
      const newNodeId = `node_${nodes.length + 1}_${Date.now()}`;
      // Default to gray style (Backlog style)
      const defaultColor = NODE_COLORS.find(c => c.id === 'gray');
      
      const newNode = {
          id: newNodeId,
          position: { x: 100, y: 100 }, // Default position, user drags it
          data: { label: 'Nouvelle étape' },
          style: { 
              backgroundColor: defaultColor.value, 
              color: 'white', 
              border: `1px solid ${defaultColor.border}`, 
              width: 150 
          },
          sourcePosition: Position.Right,
          targetPosition: Position.Left,
      };
      setNodes((nds) => [...nds, newNode]);
  };

  const deleteSelected = () => {
      if (!selectedElement) return;
      
      if (selectedElement.type === 'node') {
          setNodes((nds) => nds.filter((n) => n.id !== selectedElement.data.id));
          setEdges((eds) => eds.filter((e) => e.source !== selectedElement.data.id && e.target !== selectedElement.data.id));
      } else {
          setEdges((eds) => eds.filter((e) => e.id !== selectedElement.data.id));
      }
      setSelectedElement(null);
      setIsEditing(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-2xl w-[90vw] h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-800">Workflow: {title}</h2>
            {readOnly ? (
                <span className="px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-600 rounded-md border border-gray-200">Lecture seule</span>
            ) : (
                <button
                    onClick={addNewNode}
                    className="btn btn-primary btn-sm flex items-center gap-1.5"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Ajouter une étape
                </button>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Content Container */}
        <div className="flex-grow flex overflow-hidden">
            {/* React Flow Canvas */}
            <div className="flex-grow bg-gray-50 relative h-full">
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={readOnly ? undefined : onNodesChange}
                onEdgesChange={readOnly ? undefined : onEdgesChange}
                onConnect={readOnly ? undefined : onConnect}
                onNodeClick={readOnly ? undefined : onNodeClick}
                onEdgeClick={readOnly ? undefined : onEdgeClick}
                onPaneClick={onPaneClick}
                nodesDraggable={!readOnly}
                nodesConnectable={!readOnly}
                elementsSelectable={!readOnly}
                fitView
                attributionPosition="bottom-right"
            >
                <style>
                    {`
                        .react-flow__handle {
                            width: 24px !important;
                            height: 24px !important;
                            border-radius: 50%;
                            background-color: #94a3b8; /* slate-400 */
                            border: 3px solid white;
                            box-shadow: 0 0 0 1px #cbd5e1;
                        }
                        .react-flow__handle:hover {
                            background-color: #5a4fe0; /* iris-600 */
                            transform: scale(1.1);
                        }
                    `}
                </style>
                <Background color="#aaa" gap={16} />
                <Controls />
            </ReactFlow>
            </div>

            {/* Properties Panel (Sidebar) */}
            {!readOnly && isEditing && selectedElement && (
                <div className="w-80 bg-white border-l border-gray-200 shadow-lg flex flex-col z-10 h-full overflow-y-auto">
                    <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                        <h3 className="font-bold text-gray-800 text-sm uppercase tracking-wide">
                            {selectedElement.type === 'node' ? 'Modifier l\'étape' : 'Permission de transition'}
                        </h3>
                        <button onClick={onPaneClick} className="text-gray-400 hover:text-gray-600">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                    </div>
                    
                    <div className="p-4 space-y-6">
                        {selectedElement.type === 'node' && (
                            <div>
                                <div className="mb-4">
                                    <label className="field-label mb-2">Nom de l'étape</label>
                                    <input
                                        type="text"
                                        value={selectedElement.data.data.label}
                                        onChange={(e) => updateNodeLabel(e.target.value)}
                                        className="input"
                                    />
                                </div>
                                
                                <div>
                                    <label className="field-label mb-2">Couleur</label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {NODE_COLORS.map(color => (
                                            <button
                                                key={color.id}
                                                onClick={() => updateNodeColor(color.id)}
                                                className={`
                                                    h-8 rounded-md border shadow-sm transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-iris-500
                                                    ${selectedElement.data.style?.backgroundColor === color.value ? 'ring-2 ring-offset-1 ring-iris-500' : ''}
                                                `}
                                                style={{ backgroundColor: color.value, borderColor: color.border }}
                                                title={color.label}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {selectedElement.type === 'edge' && (
                            <div>
                                <div className="text-xs text-gray-500 mb-4 p-2 bg-blue-50 rounded border border-blue-100">
                                    Sélectionnez les rôles autorisés à effectuer cette transition. Si aucun rôle n'est sélectionné, la transition est ouverte à tous (sauf restrictions globales).
                                </div>
                                <label className="field-label mb-3">Rôles autorisés</label>
                                <div className="space-y-2">
                                    {ROLES.map(role => (
                                        <label key={role} className="flex items-center space-x-3 p-2 rounded hover:bg-gray-50 border border-transparent hover:border-gray-200 cursor-pointer">
                                            <input 
                                                type="checkbox" 
                                                checked={(selectedElement.data?.allowedRoles || []).includes(role)}
                                                onChange={() => updateEdgeRoles(role)}
                                                className="h-4 w-4 text-iris-600 focus:ring-iris-500 border-gray-300 rounded"
                                            />
                                            <span className="text-sm text-gray-700 font-medium">{role}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="pt-4 border-t border-gray-100 mt-4">
                            <button
                                onClick={deleteSelected}
                                className="btn btn-danger btn-block flex justify-center items-center"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                                Supprimer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-3 flex-shrink-0">
            <button
                onClick={onClose}
                className="btn btn-secondary"
            >
                Fermer
            </button>
            {!readOnly && (
                <button
                    onClick={() => {
                        onSave && onSave(nodes, edges);
                        onClose();
                    }}
                    className="btn btn-primary"
                >
                    Sauvegarder
                </button>
            )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowModal;
